// src/components/common/SectionHowTo.tsx
// ============================================================================
// TARJETA "CÓMO FUNCIONA" POR SECCIÓN (how-to de una sola vez, descartable)
// ============================================================================
//
// Banner explicativo que se muestra al entrar a una sección. Cuando el dueño
// lo cierra ("Entendido"), se marca en users.howtosSeen[sectionKey] = true y
// NO vuelve a aparecer (persiste entre dispositivos vía AuthContext.markHowtoSeen).
//
// Es un PATRÓN REUTILIZABLE: la semilla del futuro onboarding por secciones.
// Para añadirlo a otra sección (Gift cards, Campañas, etc.) basta con montar
// <SectionHowTo sectionKey="gift-cards" title=... steps={[...]} /> en su página.
//
// Persistencia: AuthContext (campo users.howtosSeen, ver markHowtoSeen).
// ============================================================================

import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { X, Lightbulb, Check } from 'lucide-react';

export interface HowToStep {
  /** Icono opcional (lucide). Si no se pasa, se muestra el número del paso. */
  icon?: React.ReactNode;
  title: string;
  text: React.ReactNode;
}

interface SectionHowToProps {
  /** Clave única de la sección (ej. "referrals"). Se guarda en users.howtosSeen. */
  sectionKey: string;
  title: string;
  intro?: React.ReactNode;
  steps: HowToStep[];
  /** Contenido opcional al pie (ej. un enlace a "Mi Negocio"). */
  footer?: React.ReactNode;
  /** Texto del botón de cierre. Default "Entendido". */
  dismissLabel?: string;
}

const SectionHowTo: React.FC<SectionHowToProps> = ({
  sectionKey,
  title,
  intro,
  steps,
  footer,
  dismissLabel = 'Entendido',
}) => {
  const { currentUser, markHowtoSeen } = useAuth();
  const [closing, setClosing] = useState(false);

  // Ya lo cerró antes → no renderizar nada.
  if (currentUser?.howtosSeen?.[sectionKey]) return null;

  const handleDismiss = () => {
    setClosing(true);
    // Pequeño delay para la animación de salida antes de persistir/ocultar.
    setTimeout(() => { void markHowtoSeen(sectionKey); }, 200);
  };

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-500/10 dark:to-cyan-500/10 shadow-sm transition-all duration-200 ${
        closing ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0 animate-fade-in-up'
      }`}
    >
      {/* Cerrar */}
      <button
        onClick={handleDismiss}
        aria-label="Cerrar explicación"
        className="absolute right-3 top-3 text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-200 transition-colors p-1 rounded-full hover:bg-white/50 dark:hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1 pr-8">
          <div className="bg-emerald-100 dark:bg-emerald-500/20 p-1.5 rounded-lg">
            <Lightbulb className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-base font-bold text-emerald-900 dark:text-emerald-200">{title}</h3>
        </div>

        {intro && (
          <p className="text-sm text-emerald-800/90 dark:text-emerald-300/90 mb-4 mt-1">{intro}</p>
        )}

        {/* Pasos */}
        <ol className="space-y-3 mt-3">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shadow-sm">
                {step.icon ?? i + 1}
              </div>
              <div className="text-sm pt-0.5">
                <span className="font-semibold text-emerald-900 dark:text-emerald-200">{step.title}</span>
                {step.title && ' '}
                <span className="text-emerald-800/90 dark:text-emerald-300/90">{step.text}</span>
              </div>
            </li>
          ))}
        </ol>

        {footer && <div className="mt-4 text-sm text-emerald-800/90 dark:text-emerald-300/90">{footer}</div>}

        {/* Acción */}
        <div className="mt-5 flex justify-end">
          <button
            onClick={handleDismiss}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Check className="h-4 w-4 mr-1.5" />
            {dismissLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionHowTo;
